import { Mutation, TransferInfoActionType } from '@varo/graphql-schema-types';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { PLAID_RELINK_SOURCE_NAMES } from 'features/plaid/constants/PlaidSourceNames';
import usePlaidLauncher from 'features/plaid/hooks/usePlaidLauncher';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import RemoveExternalAccount from 'services/graphql/mutations/RemoveExternalAccount';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { PlaidIntent } from 'shared/hooks/usePlaidV2/types';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { GlobalUIContext } from 'shared/providers/globalUIProvider/GlobalUIContextProvider/GlobalUIContextProvider';
import { useIdempotencyToken } from 'shared/utils/idempotencyTokens/idempotencyTokenUtils';
import useVaroOnboardingFlow from 'shared/utils/useVaroOnboardingFlow/useVaroOnboardingFlow';
import { useMutation } from 'urql';

interface ButtonProps {
  title: string;
  onPress: () => Promise<void> | void;
  isLoading?: boolean;
}

interface ErrorScreenButton {
  primary: ButtonProps;
  secondary?: ButtonProps;
}

type ExternalAccountErrorSearchParams = {
  accountId: string;
  actionType: TransferInfoActionType;
  actionCta: string;
  actionTitle: string;
  actionMessage: string;
};

const ExternalAccountError = () => {
  const router = useRouter();
  const flow = useVaroOnboardingFlow();
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'transferStrings',
  });
  const { setSnackBarMessage } = useContext(GlobalUIContext);
  const { idempotencyToken } = useIdempotencyToken('0');

  const [{ fetching: removeAccountLoading }, removeExternalAccount] =
    useMutation<Mutation>(RemoveExternalAccount);

  const { accountId, actionType, actionCta, actionTitle, actionMessage } =
    useLocalSearchParams<ExternalAccountErrorSearchParams>();

  const returnToTransferMoney = () => {
    router.dismissTo({
      pathname: `/${flow}/transfer/transfer-money`,
      params: { refreshAccounts: 'true' },
    });
  };

  const {
    startLaunch: startRelink,
    cancelLaunch: cancelRelink,
    error: relinkTokenError,
  } = usePlaidLauncher(
    accountId ? { intent: PlaidIntent.ReLink, accountId } : undefined,
    {
      sourceName: PLAID_RELINK_SOURCE_NAMES.MoveMoneyTransfer,
      onResult: (result) => {
        if (result.status === 'success') {
          returnToTransferMoney();
          return;
        }

        if (result.status === 'error' || result.status === 'blocked') {
          setSnackBarMessage?.(translatedText('relinkAccountFailed'));
        }
      },
    },
  );

  useViewDidFocusScreenAnalyticsEvent({
    key: 'accountNotLinked',
    customAttributes: { name: 'Account Not Linked' },
  });

  useEffect(() => {
    if (relinkTokenError) {
      setSnackBarMessage?.(translatedText('relinkAccountError'));
      cancelRelink();
    }
  }, [cancelRelink, relinkTokenError, setSnackBarMessage, translatedText]);

  const cancelButton: ButtonProps = {
    title: translatedText('cancelButton'),
    onPress: () => router.back(),
  };

  const getErrorScreenButtons = (): ErrorScreenButton | null => {
    switch (actionType) {
      case TransferInfoActionType.RemoveAccount:
        return {
          primary: {
            title: actionCta,
            onPress: async () => {
              await removeExternalAccount({
                idempotencyId: idempotencyToken,
                id: accountId,
              });
              returnToTransferMoney();
            },
            isLoading: removeAccountLoading,
          },
          secondary: cancelButton,
        };
      case TransferInfoActionType.CreateRelinkAccount:
      case TransferInfoActionType.UpdateRelinkAccount:
        return {
          primary: {
            title: actionCta,
            onPress: startRelink,
          },
          secondary: cancelButton,
        };
      default:
        return { primary: { ...cancelButton, title: actionCta } };
    }
  };

  const errorScreenButtons = getErrorScreenButtons();

  return (
    <ErrorScreen
      screenType={ErrorScreenType.Error}
      messageHeadline={actionTitle}
      messageBody={actionMessage}
      primaryButton={errorScreenButtons?.primary}
      secondaryButton={errorScreenButtons?.secondary}
    />
  );
};

export default ExternalAccountError;
